// A small single-series chart over time (bars or a line), drawn as SVG: recessive grid, three
// round y ticks, hour labels, and a hover tooltip showing the exact value. One series per chart
// (no legend needed: the title names it); the data colour is the app accent.
import { useEffect, useMemo, useRef, useState } from 'react';

export interface Point { t: string; v: number | null }

const H = 170;
const PAD = { l: 52, r: 12, t: 10, b: 24 };
const fmtHour = new Intl.DateTimeFormat('en-IE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Dublin' });

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

export function MiniChart({ title, points, kind, format, stepMs, extraLine }: {
  title: string;
  points: Point[];
  kind: 'bar' | 'line';
  format: (v: number) => string;
  stepMs: number;               // bucket size, e.g. 1 h for bars
  extraLine?: { value: number; label: string };  // a reference line, e.g. the 70 % mark
}) {
  const [hover, setHover] = useState<number | null>(null);
  const ref = useRef<SVGSVGElement>(null);
  // Drawn at the box's real width, so text keeps its size on a phone and on a wide screen.
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(260, Math.round(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { x, y, max, t0, t1 } = useMemo(() => {
    const now = Date.now();
    const t1 = Math.ceil(now / stepMs) * stepMs;
    const t0 = t1 - 24 * 3600_000;
    const top = Math.max(...points.map((p) => p.v ?? 0), extraLine?.value ?? 0);
    const max = niceMax(top * 1.1);
    const x = (t: number) => PAD.l + ((t - t0) / (t1 - t0)) * (W - PAD.l - PAD.r);
    const y = (v: number) => PAD.t + (1 - v / max) * (H - PAD.t - PAD.b);
    return { x, y, max, t0, t1 };
  }, [points, stepMs, extraLine, W]);

  const inRange = points.filter((p) => Date.parse(p.t) >= t0 && Date.parse(p.t) < t1);
  const slot = ((W - PAD.l - PAD.r) * stepMs) / (t1 - t0);
  const barW = Math.max(2, Math.min(24, slot - 2));
  const ticks = [0, max / 2, max];
  // Hour labels every 3 h on wide charts, 6 h on narrow ones; the last one is "now".
  const every = W > 560 ? 3 : 6;
  const hours = Array.from({ length: 24 / every + 1 }, (_, i) => t0 + i * every * 3600_000);
  const hovered = hover === null ? null : inRange[hover];

  const onMove = (e: React.MouseEvent) => {
    const r = ref.current!.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    let best = -1;
    let dist = Infinity;
    inRange.forEach((p, i) => {
      const d = Math.abs(x(Date.parse(p.t) + (kind === 'bar' ? stepMs / 2 : 0)) - px);
      if (d < dist) { dist = d; best = i; }
    });
    setHover(best >= 0 && dist < slot * 2 + 12 ? best : null);
  };

  const linePath = kind === 'line'
    ? inRange.filter((p) => p.v !== null).map((p, i) => `${i ? 'L' : 'M'}${x(Date.parse(p.t)).toFixed(1)},${y(p.v!).toFixed(1)}`).join('')
    : '';

  return (
    <figure className="mchart">
      <figcaption>{title}</figcaption>
      <div className="mchart-box" ref={box}>
        <svg ref={ref} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          {ticks.map((v) => (
            <g key={v}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} className="mchart-grid" />
              <text x={PAD.l - 6} y={y(v) + 4} textAnchor="end" className="mchart-tick">{format(v)}</text>
            </g>
          ))}
          {hours.map((t, i) => (
            <text key={t} x={x(t)} y={H - 6} textAnchor={i === 0 ? 'start' : i === hours.length - 1 ? 'end' : 'middle'} className="mchart-tick">
              {i === hours.length - 1 ? 'now' : fmtHour.format(t)}
            </text>
          ))}
          {extraLine && (
            <g>
              <line x1={PAD.l} x2={W - PAD.r} y1={y(extraLine.value)} y2={y(extraLine.value)} className="mchart-ref" />
              <text x={W - PAD.r} y={y(extraLine.value) - 4} textAnchor="end" className="mchart-tick">{extraLine.label}</text>
            </g>
          )}
          {kind === 'bar' && inRange.map((p, i) => {
            if (!p.v) return null;
            const cx = x(Date.parse(p.t) + stepMs / 2);
            const top = y(p.v);
            const h = y(0) - top;
            const r = Math.min(4, h, barW / 2);
            return (
              <path key={p.t} className={`mchart-bar${hover === i ? ' on' : ''}`}
                d={`M${cx - barW / 2},${y(0)}V${top + r}Q${cx - barW / 2},${top} ${cx - barW / 2 + r},${top}H${cx + barW / 2 - r}Q${cx + barW / 2},${top} ${cx + barW / 2},${top + r}V${y(0)}Z`} />
            );
          })}
          {kind === 'line' && <path d={linePath} className="mchart-line" />}
          {hovered && hovered.v !== null && (
            <g>
              <line x1={x(Date.parse(hovered.t) + (kind === 'bar' ? stepMs / 2 : 0))} x2={x(Date.parse(hovered.t) + (kind === 'bar' ? stepMs / 2 : 0))} y1={PAD.t} y2={y(0)} className="mchart-cross" />
              {kind === 'line' && <circle cx={x(Date.parse(hovered.t))} cy={y(hovered.v)} r={4} className="mchart-dot" />}
            </g>
          )}
        </svg>
        {hovered && hovered.v !== null && (
          <div className="mchart-tip" style={{ left: `${(x(Date.parse(hovered.t) + (kind === 'bar' ? stepMs / 2 : 0)) / W) * 100}%` }}>
            <span className="muted">{fmtHour.format(Date.parse(hovered.t))}{kind === 'bar' ? `–${fmtHour.format(Date.parse(hovered.t) + stepMs)}` : ''}</span>
            <strong>{format(hovered.v)}</strong>
          </div>
        )}
      </div>
    </figure>
  );
}
